import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import type { Treatment, TreatmentCategory } from '../types';
import { useAuth } from '../hooks/useAuth';

const categoryLabels: Record<TreatmentCategory, string> = {
  'pestanas': 'Pestañas',
  'lifting': 'Lifting',
  'cejas': 'Cejas',
  'micropigmentacion-cejas': 'Micropigmentación de Cejas',
  'micropigmentacion-labios': 'Micropigmentación de Labios',
  'micropigmentacion-ojos': 'Micropigmentación de Línea de Ojos',
  'manicura': 'Manicura',
  'pedicura': 'Pedicura',
  'tratamientos-faciales': 'Tratamientos Faciales',
  'corte-pelo':'Corte de Pelo',
  'tinte-cabello':'Tinte de Cabello'
};

export function TreatmentDetail() {
  const navigate = useNavigate();
  const { treatmentId } = useParams();
  const { user } = useAuth();
  const [treatment, setTreatment] = useState<Treatment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!treatmentId) {
      setError('Falta el id del tratamiento');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    fetch(`/server/fichas_cliente_sisters_peluqueros_function/treatment/${encodeURIComponent(treatmentId)}`)
      .then(async response => {
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'No se pudo cargar el tratamiento');
        }

        return data as {
          Tratamientos: {
            ROWID: string;
            CREATORID?: string;
            Specialist: string;
            Categoria: Treatment['category'];
            Estado?: string;
            FotosAntes?: string | null;
            FotoDespues?: string | null;
            FormData?: string;
            ID_Cliente: string;
            Fecha: string;
            IsDraft?: boolean;
          };
        };
      })
      .then(({ Tratamientos: rawTreatment }) => {
        let parsedFormData: Record<string, any> = {};

        try {
          parsedFormData = rawTreatment.FormData ? JSON.parse(rawTreatment.FormData) : {};
        } catch (parseError) {
          console.error('Error al parsear FormData del tratamiento:', parseError);
        }

        const photos = [
          rawTreatment.FotosAntes
            ? { id: `${rawTreatment.ROWID}-antes`, url: rawTreatment.FotosAntes, type: 'antes' as const }
            : null,
          rawTreatment.FotoDespues
            ? { id: `${rawTreatment.ROWID}-despues`, url: rawTreatment.FotoDespues, type: 'despues' as const }
            : null,
        ].filter(Boolean) as Treatment['photos'];

        setTreatment({
          id: rawTreatment.ROWID,
          clientId: rawTreatment.ID_Cliente,
          category: rawTreatment.Categoria,
          date: rawTreatment.Fecha,
          specialist: rawTreatment.Specialist,
          creatorId: rawTreatment.CREATORID,
          photos,
          formData: parsedFormData,
          isDraft: Boolean(rawTreatment.IsDraft),
        });
      })
      .catch(fetchError => {
        console.error('Error al obtener el tratamiento:', fetchError);
        setError(fetchError instanceof Error ? fetchError.message : 'Error al obtener el tratamiento');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [treatmentId]);

  if (isLoading) {
    return <div className="p-6 text-gray-500">Cargando tratamiento...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500">{error}</div>;
  }

  if (!treatment) {
    return <div>Tratamiento no encontrado</div>;
  }

  const photoBefore = treatment.photos.find(p => p.type === 'antes');
  const photoAfter = treatment.photos.find(p => p.type === 'despues');
  const creatorName = treatment.creatorId && user?.id === treatment.creatorId
    ? user.name
    : treatment.creatorName || treatment.creatorId || 'No disponible';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/client/${treatment.clientId}`)}
              className="text-gray-600 hover:text-[#E91E8C]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl text-[#2d2d2d]">Detalle del Tratamiento</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Información General */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="text-2xl mb-4 text-[#2d2d2d]">
            {categoryLabels[treatment.category]}
          </h2>
          
          {treatment.isDraft && (
            <Button
              onClick={() => navigate(`/client/${treatment.clientId}/treatment/${treatment.id}/edit`)}
              className="mb-4 bg-[#E91E8C] hover:bg-[#d01a7d] text-white"
            >
              Editar borrador
            </Button>
          )}

          <div className="flex gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{new Date(treatment.date).toLocaleDateString('es-ES')}</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{treatment.specialist}</span>
            </div>
          </div>
        </div>

        {/* Fotografías */}
        {(photoBefore || photoAfter) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="mb-4">Fotografías</h3>
            <div className="grid grid-cols-2 gap-4">
              {photoBefore && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Antes</p>
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={photoBefore.url} 
                      alt="Antes" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
              {photoAfter && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Después</p>
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img 
                      src={photoAfter.url} 
                      alt="Después" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Datos del Tratamiento */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="mb-4">Datos del Tratamiento</h3>
          <div className="space-y-3">
            <div className="border-b border-gray-100 pb-3">
              <p className="text-sm text-gray-600 mb-1">Usuario creador</p>
              <p className="text-[#2d2d2d]">{creatorName}</p>
            </div>
            {Object.entries(treatment.formData).map(([key, value]) => (
              <div key={key} className="border-b border-gray-100 pb-3 last:border-0">
                <p className="text-sm text-gray-600 mb-1 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </p>
                <p className="text-[#2d2d2d]">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
